import { Fields } from 'models/IntegrationsConfig'
import { useMemo } from 'react'
import { api } from './index'

export type IntegrationsConfigResponse = {
  merchant_id: number
  integrations_config: Fields['integrations_config']
  // Absent until nexus's feedback-provider detection deploys. null is a real
  // answer (no third-party tool), not "not deployed". Once nexus#2291 is in
  // production, make this required and delete legacyToggleProvider and the
  // fallback to it.
  feedback_provider?: ThirdPartyFeedbackProvider | null
}

export const integrationsConfigApi = api.injectEndpoints({
  endpoints: build => ({
    getIntegrationsConfig: build.query<IntegrationsConfigResponse, void>({
      query: () => '/integrations',
      providesTags: ['IntegrationsConfig'],
    }),
  }),
})

export const { useGetIntegrationsConfigQuery } = integrationsConfigApi

// Every partner the Integration Center lists under its `feedback` category,
// minus `thanx`, which is the default Thanx tool rather than a partner. Kept in
// step with `utilities/integrations` by a test: the legacy toggle fallback
// cannot see a partner missing here, and reports no third-party tool instead.
//
// Order is the fallback's tie-break. The Integration Center allows at most one
// partner, but nothing in the toggle payload enforces it, so two `enabled`
// partners resolve to the FIRST entry here rather than to whichever key the
// server happened to serialise first. nexus's detected provider needs none.
export const THIRD_PARTY_FEEDBACK_PROVIDERS = [
  'tattle',
  'ovation',
  'yumpingo',
  'guestxm',
  'momos',
  'black_box_intelligence',
] as const

export type ThirdPartyFeedbackProvider =
  typeof THIRD_PARTY_FEEDBACK_PROVIDERS[number]

// `enabled` and `auto` both mean connected; `disabled` and an absent key mean
// not connected. The per-provider value is a bare string, not a boolean and not
// an object (verified against merchant-api's integrations request spec).
const CONNECTED_STATES = ['enabled', 'auto']

const legacyToggleProvider = (
  feedback: Fields['integrations_config']['feedback'] | undefined
): ThirdPartyFeedbackProvider | null =>
  (feedback &&
    THIRD_PARTY_FEEDBACK_PROVIDERS.find(provider =>
      CONNECTED_STATES.includes(feedback[provider])
    )) ??
  null

/**
 * Which third-party feedback tool this merchant has connected: the tool, `null`
 * when only Thanx is on, or `undefined` until a response arrives. A failed
 * request with no earlier answer stays `undefined`, so no caller can read an
 * outage as "no tool".
 *
 * Same shape as `useBrameIntegration` below, over the same single
 * `GET /integrations` request — no new endpoint.
 */
export const useThirdPartyFeedbackIntegration = () => {
  const { data, isLoading, error } = useGetIntegrationsConfigQuery()

  const thirdPartyFeedbackProvider = useMemo(():
    | ThirdPartyFeedbackProvider
    | null
    | undefined => {
    if (!data) return undefined

    // The Admin toggle is set by hand and often wrong, so nexus's detected
    // provider wins whenever the key is present, null included. The toggle
    // answers only while the key is absent, so deploying this ahead of nexus
    // changes nothing.
    if (data.feedback_provider !== undefined) return data.feedback_provider

    return legacyToggleProvider(data.integrations_config?.feedback)
  }, [data])

  return { thirdPartyFeedbackProvider, isLoading, error }
}

export const useBrameIntegration = () => {
  const { data, isLoading, error } = useGetIntegrationsConfigQuery()

  const brameIntegrationEnabled = useMemo(() => {
    return data?.integrations_config?.games?.brame === 'enabled'
  }, [data])

  return {
    brameIntegrationEnabled,
    isLoading,
    error,
    integrationConfig: data,
  }
}

export const useIntegrationsConfig = () => {
  const { data, isLoading, error } = useGetIntegrationsConfigQuery()

  const integrationConfig = useMemo((): Fields | null => {
    if (!data) return null

    return {
      id: `integrations_config_${data.merchant_id}`,
      integrations_config: data.integrations_config,
    }
  }, [data])

  return {
    integrationConfig,
    isLoading,
    error,
  }
}
